// IssueLinkPicker — cross-register link selector for an issue. Shows the
// current selection as removable chips and an expandable panel with scope
// tabs (All / Deliverables / Milestones / KPIs), a search box, and a
// multi-select list. The KPI tab additionally lists each KPI's measurements
// (the canonical "KPI period" link) as expandable children — there is no
// separate `kpi_period` tracking type, so a per-period link points at the
// real kpi-measurement row.
//
// Composite-unique: identity is (targetType, targetId). Toggling an already-
// selected row removes it; selecting a new row appends. The backend enforces
// the same uniqueness at the schema boundary as defence in depth.
//
// Data: reuses useTrackingItems for each of the four target types. The
// register reads are cached (React Query) and small per-contract, so opening
// the panel does not trigger a flurry of network calls when the user is
// editing several issues.

import { useMemo, useState } from "react";
import { Check, Plus, Search, X } from "lucide-react";
import { useTrackingItems } from "../../../../hooks/useTracking";
import { labelForIssueLink } from "./labelForIssueLink";
import type {
  IssueLink,
  IssueLinkTargetType,
  Kpi,
  KpiMeasurement,
  Deliverable,
  Milestone,
  TrackingItem,
} from "../../../../types";

type Scope = "all" | "deliverable" | "milestone" | "kpi";

const SCOPE_TABS: { value: Scope; label: string }[] = [
  { value: "all", label: "All" },
  { value: "deliverable", label: "Deliverables" },
  { value: "milestone", label: "Milestones" },
  { value: "kpi", label: "KPIs" },
];

const TARGET_TYPE_LABELS: Record<IssueLinkTargetType, string> = {
  deliverable: "Deliverable",
  milestone: "Milestone",
  kpi: "KPI",
  "kpi-measurement": "KPI period",
};

export interface IssueLinkPickerProps {
  contractId: string;
  value: IssueLink[];
  onChange: (next: IssueLink[]) => void;
}

interface RowDescriptor {
  targetType: IssueLinkTargetType;
  targetId: string;
  label: string;
  /** Secondary text shown beneath the label (e.g. KPI number, measurement period). */
  meta?: string;
}

function linkKey(link: { targetType: string; targetId: string }): string {
  return `${link.targetType} ${link.targetId}`;
}

function isDeliverable(i: TrackingItem): i is Deliverable {
  return i.type === "deliverable";
}
function isMilestone(i: TrackingItem): i is Milestone {
  return i.type === "milestone";
}
function isKpi(i: TrackingItem): i is Kpi {
  return i.type === "kpi";
}
function isKpiMeasurement(i: TrackingItem): i is KpiMeasurement {
  return i.type === "kpi-measurement";
}

export default function IssueLinkPicker({
  contractId,
  value,
  onChange,
}: IssueLinkPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [scope, setScope] = useState<Scope>("all");
  const [search, setSearch] = useState("");
  const [expandedKpiIds, setExpandedKpiIds] = useState<Set<string>>(new Set());

  // Each list query is gated on the panel being open so a contract with
  // hundreds of items doesn't pre-fetch when the picker is collapsed. The
  // queries are cached by useTrackingItems so re-opening the panel is free.
  const deliverableQuery = useTrackingItems(contractId, "deliverable", undefined, {
    enabled: isOpen,
  });
  const milestoneQuery = useTrackingItems(contractId, "milestone", undefined, {
    enabled: isOpen,
  });
  const kpiQuery = useTrackingItems(contractId, "kpi", undefined, {
    enabled: isOpen,
  });
  const measurementQuery = useTrackingItems(
    contractId,
    "kpi-measurement",
    undefined,
    { enabled: isOpen },
  );

  const deliverables = useMemo<Deliverable[]>(
    () => (deliverableQuery.data?.items ?? []).filter(isDeliverable),
    [deliverableQuery.data],
  );
  const milestones = useMemo<Milestone[]>(
    () => (milestoneQuery.data?.items ?? []).filter(isMilestone),
    [milestoneQuery.data],
  );
  const kpis = useMemo<Kpi[]>(
    () => (kpiQuery.data?.items ?? []).filter(isKpi),
    [kpiQuery.data],
  );
  const measurements = useMemo<KpiMeasurement[]>(
    () => (measurementQuery.data?.items ?? []).filter(isKpiMeasurement),
    [measurementQuery.data],
  );

  const measurementsByKpi = useMemo(() => {
    const map = new Map<string, KpiMeasurement[]>();
    for (const m of measurements) {
      const list = map.get(m.parentId) ?? [];
      list.push(m);
      map.set(m.parentId, list);
    }
    // Most-recent period first within each KPI.
    for (const list of map.values()) {
      list.sort((a, b) => b.period.localeCompare(a.period));
    }
    return map;
  }, [measurements]);

  const selectedKeys = useMemo(
    () => new Set(value.map((l) => linkKey(l))),
    [value],
  );

  const toggleLink = (link: IssueLink) => {
    const key = linkKey(link);
    if (selectedKeys.has(key)) {
      onChange(value.filter((l) => linkKey(l) !== key));
    } else {
      onChange([...value, link]);
    }
  };

  const removeLink = (link: IssueLink) => {
    onChange(value.filter((l) => linkKey(l) !== linkKey(link)));
  };

  const toggleKpiExpansion = (kpiId: string) => {
    setExpandedKpiIds((prev) => {
      const next = new Set(prev);
      if (next.has(kpiId)) next.delete(kpiId);
      else next.add(kpiId);
      return next;
    });
  };

  // Build a flat row list for the current scope + search query. KPI rows in
  // the kpi scope are interleaved with their measurement rows when expanded
  // so the list reads as a single scrollable column.
  const filteredRows = useMemo<RowDescriptor[]>(() => {
    const needle = search.trim().toLowerCase();
    const matches = (s: string) => !needle || s.toLowerCase().includes(needle);

    const rows: RowDescriptor[] = [];

    if (scope === "all" || scope === "deliverable") {
      for (const d of deliverables) {
        if (matches(d.name)) {
          rows.push({
            targetType: "deliverable",
            targetId: d.id,
            label: d.name,
          });
        }
      }
    }
    if (scope === "all" || scope === "milestone") {
      for (const m of milestones) {
        if (matches(m.name)) {
          rows.push({
            targetType: "milestone",
            targetId: m.id,
            label: m.name,
          });
        }
      }
    }
    if (scope === "all" || scope === "kpi") {
      for (const k of kpis) {
        const kpiMatches = matches(k.name) || matches(String(k.kpiNumber));
        if (kpiMatches) {
          rows.push({
            targetType: "kpi",
            targetId: k.id,
            label: k.name,
            meta: `KPI ${k.kpiNumber}`,
          });
        }
        const isExpanded = scope === "kpi" && expandedKpiIds.has(k.id);
        if (isExpanded) {
          for (const m of measurementsByKpi.get(k.id) ?? []) {
            if (matches(m.period) || matches(m.name)) {
              rows.push({
                targetType: "kpi-measurement",
                targetId: m.id,
                label: m.name || `Period ${m.period}`,
                meta: m.name ? `Period ${m.period}` : undefined,
              });
            }
          }
        }
      }
    }
    return rows;
  }, [scope, search, deliverables, milestones, kpis, measurementsByKpi, expandedKpiIds]);

  // Selected chips need labels that survive the panel being collapsed (the
  // queries are gated on `enabled`, so when the panel is closed we may not
  // have a fresh name available). The shared resolver falls back to the
  // targetId so an existing link is always identifiable, and keeps the chip
  // labels in step with the drawer's "Linked to" strip.
  const labelFor = (link: IssueLink): string =>
    labelForIssueLink(link, { deliverables, milestones, kpis, measurements });

  return (
    <div>
      <div className="label">Linked items (optional)</div>

      {value.length > 0 ? (
        <ul
          data-testid="issue-link-picker-chips"
          className="mb-2 flex flex-wrap gap-1.5"
        >
          {value.map((link) => (
            <li
              key={linkKey(link)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-caption text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200"
            >
              <span className="font-medium">
                {TARGET_TYPE_LABELS[link.targetType]}:
              </span>
              <span className="truncate max-w-[16rem]">{labelFor(link)}</span>
              <button
                type="button"
                onClick={() => removeLink(link)}
                aria-label={`Remove ${TARGET_TYPE_LABELS[link.targetType]} link`}
                className="ml-0.5 inline-flex h-5 w-5 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
              >
                <X aria-hidden="true" className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-2 text-caption text-slate-500 dark:text-slate-400">
          No linked items yet.
        </p>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
        aria-controls="issue-link-picker-panel"
        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        {isOpen ? (
          <Check aria-hidden="true" className="h-3.5 w-3.5" />
        ) : (
          <Plus aria-hidden="true" className="h-3.5 w-3.5" />
        )}
        {isOpen ? "Done" : "Add link"}
      </button>

      {isOpen && (
        <div
          id="issue-link-picker-panel"
          data-testid="issue-link-picker-panel"
          className="mt-2 rounded-md border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800/40"
        >
          {/* Segmented button group rather than role=tablist — implementing the
              full WAI-ARIA roving-tabindex + arrow-key pattern is outside
              this surface's scope, and an incorrect tablist is worse for
              assistive tech than an honest button row. `aria-pressed` carries
              the selected state without making any keyboard-pattern promise. */}
          <div
            role="group"
            aria-label="Link scope"
            className="mb-2 inline-flex rounded-md border border-slate-200 p-0.5 dark:border-slate-700"
          >
            {SCOPE_TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                aria-pressed={scope === t.value}
                onClick={() => setScope(t.value)}
                className={`rounded px-2 py-0.5 text-caption focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 ${
                  scope === t.value
                    ? "bg-brand-600 text-white"
                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="relative mb-2">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              aria-label="Search items"
              className="input w-full pl-7"
            />
          </div>

          <ul
            data-testid="issue-link-picker-rows"
            className="max-h-64 space-y-0.5 overflow-y-auto"
          >
            {filteredRows.length === 0 ? (
              <li className="px-2 py-3 text-center text-body-sm text-slate-500 dark:text-slate-400">
                No matches.
              </li>
            ) : (
              filteredRows.map((row) => {
                const key = linkKey(row);
                const checked = selectedKeys.has(key);
                const isKpiRow =
                  scope === "kpi" && row.targetType === "kpi";
                return (
                  <li
                    key={key}
                    className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${
                      row.targetType === "kpi-measurement" ? "pl-6" : ""
                    } hover:bg-slate-50 dark:hover:bg-slate-700/40`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        toggleLink({
                          targetType: row.targetType,
                          targetId: row.targetId,
                        })
                      }
                      aria-label={`${TARGET_TYPE_LABELS[row.targetType]} ${row.label}`}
                      className="h-4 w-4 accent-brand-600"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-body-sm text-slate-800 dark:text-slate-100">
                        {row.label}
                      </div>
                      {row.meta && (
                        <div className="truncate text-caption text-slate-500 dark:text-slate-400">
                          {row.meta}
                        </div>
                      )}
                    </div>
                    {isKpiRow && (
                      <button
                        type="button"
                        onClick={() => toggleKpiExpansion(row.targetId)}
                        aria-expanded={expandedKpiIds.has(row.targetId)}
                        aria-label={`Toggle measurements for ${row.label}`}
                        className="rounded px-1.5 py-0.5 text-caption text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
                      >
                        {expandedKpiIds.has(row.targetId)
                          ? "Hide periods"
                          : "Show periods"}
                      </button>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
